import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '../components/ui/dialog';
import { 
  Search, Plus, FileText, Building2, 
  ChevronRight, X, Calendar,
  TrendingUp, ArrowUpDown, Archive, Trash2
} from 'lucide-react';
import { MainLayout } from '../components/MainLayout';
import { formatCurrencyCompact } from '../utils/formatCurrency';
import { toast } from 'sonner';

const STATUS_CONFIG = {
  new: { label: 'New', color: 'bg-sky-500', textColor: 'text-sky-700' },
  under_review: { label: 'Under Review', color: 'bg-amber-500', textColor: 'text-amber-700' },
  decision_made: { label: 'Decision Made', color: 'bg-violet-500', textColor: 'text-violet-700' },
  quoted: { label: 'Quoted', color: 'bg-emerald-500', textColor: 'text-emerald-700' },
  won: { label: 'Won', color: 'bg-green-600', textColor: 'text-green-700' },
  lost: { label: 'Lost', color: 'bg-red-500', textColor: 'text-red-700' },
  archived: { label: 'Archived', color: 'bg-slate-400', textColor: 'text-slate-600' },
};

const SECTOR_LABELS = {
  commercial_fitout: 'Commercial Fit-out',
  commercial_refurb: 'Commercial Refurbishment',  // Keep for historical display
  commercial_office: 'Commercial Office',
  residential: 'Residential',
  retail: 'Retail',
  healthcare: 'Healthcare',
  education: 'Education',
  other: 'Other',
};

// Helper to format sector display (handles "Other" with custom name)
const formatSectorDisplay = (enquiry) => {
  if (enquiry.sector === 'other' && enquiry.sector_custom_name) {
    return `Other (${enquiry.sector_custom_name})`;
  }
  return SECTOR_LABELS[enquiry.sector] || enquiry.sector;
};

export const EnquiriesListPage = () => {
  const { user, authFetch } = useAuth();
  const navigate = useNavigate();
  const [enquiries, setEnquiries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [sectorFilter, setSectorFilter] = useState('all');
  const [sortBy, _setSortBy] = useState('created_at');
  const [sortOrder, setSortOrder] = useState('desc');
  const [deleteModal, setDeleteModal] = useState({ open: false, enquiry: null });
  const [deleting, setDeleting] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const [decisionFilter, setDecisionFilter] = useState(searchParams.get('decision') || '');
  const [decisionFilteredEnquiries, setDecisionFilteredEnquiries] = useState([]);

  const isDirector = user?.role === 'director';

  const DECISION_LABELS = {
    quote: 'Quote',
    quote_approval_required: 'Approval Required',
    do_not_quote: 'Do Not Quote',
    do_not_quote_yet: 'DNQ Yet',
    pause: 'Pause'
  };

  // Load enquiries by decision filter if present
  const loadDecisionFilteredEnquiries = useCallback(async (outcome) => {
    if (!outcome) {
      setDecisionFilteredEnquiries([]);
      return;
    }
    try {
      const res = await authFetch(`/dashboard/enquiries-by-decision/${outcome}`);
      if (res.ok) {
        const data = await res.json();
        setDecisionFilteredEnquiries(data.enquiries || []);
      }
    } catch (err) {
      console.error('Load decision filter error:', err);
    }
  }, [authFetch]);

  useEffect(() => {
    loadEnquiries();
    const decisionParam = searchParams.get('decision');
    if (decisionParam) {
      setDecisionFilter(decisionParam);
      loadDecisionFilteredEnquiries(decisionParam);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, loadDecisionFilteredEnquiries]);

  const clearDecisionFilter = () => {
    setDecisionFilter('');
    setDecisionFilteredEnquiries([]);
    setSearchParams({});
  };

  const loadEnquiries = async () => {
    try {
      const res = await authFetch('/enquiries');
      if (res.ok) {
        const data = await res.json();
        setEnquiries(data.enquiries || []);
      }
    } catch (err) {
      console.error('Load error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteEnquiry = async (permanent = false) => {
    if (!deleteModal.enquiry) return;
    
    setDeleting(true);
    try {
      const res = await authFetch(
        `/enquiries/${deleteModal.enquiry.id}?permanent=${permanent}`,
        { method: 'DELETE' }
      );
      
      if (res.ok) {
        toast.success(permanent ? 'Enquiry permanently deleted' : 'Enquiry archived');
        setDeleteModal({ open: false, enquiry: null });
        loadEnquiries(); // Refresh list
      } else {
        const data = await res.json();
        toast.error(data.detail || 'Failed to delete');
      }
    } catch (err) {
      toast.error('Failed to delete enquiry');
    } finally {
      setDeleting(false);
    }
  };

  // Use decision filtered enquiries if filter is active, otherwise use regular enquiries
  const baseEnquiries = decisionFilter && decisionFilteredEnquiries.length > 0 
    ? decisionFilteredEnquiries 
    : enquiries;

  const filteredEnquiries = baseEnquiries
    .filter(e => {
      const matchesSearch = 
        e.project_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        e.client_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        e.reference_number?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        e.client_company?.toLowerCase().includes(searchQuery.toLowerCase());
      
      const matchesStatus = statusFilter === 'all' || e.status === statusFilter;
      const matchesSector = sectorFilter === 'all' || e.sector === sectorFilter;
      
      return matchesSearch && matchesStatus && matchesSector;
    })
    .sort((a, b) => {
      const aVal = a[sortBy] || '';
      const bVal = b[sortBy] || '';
      const order = sortOrder === 'asc' ? 1 : -1;
      return aVal > bVal ? order : -order;
    });

  const statusCounts = Object.keys(STATUS_CONFIG).reduce((acc, status) => {
    acc[status] = enquiries.filter(e => e.status === status).length;
    return acc;
  }, {});

  const totalValue = enquiries.reduce((sum, e) => sum + (e.estimated_value || 0), 0);

  if (loading) {
    return (
      <MainLayout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-pulse text-slate-500">Loading...</div>
        </div>
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      <div className="space-y-6 max-w-full overflow-x-hidden" data-testid="enquiries-list-page">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="font-heading text-2xl md:text-3xl font-bold text-[#1e3a5f]">
              Enquiries
            </h1>
            <p className="text-slate-500 mt-1">
              {enquiries.length} total • {formatCurrencyCompact(totalValue)} pipeline value
            </p>
          </div>
          
          <div className="flex items-center gap-3">
            {isDirector && (
              <Button
                variant="outline"
                onClick={() => navigate('/enquiries/archived')}
                className="rounded-full border-slate-300 text-slate-600 hover:bg-slate-50"
                data-testid="view-archived-btn"
              >
                <Archive className="w-4 h-4 mr-2" />
                Archived
              </Button>
            )}
            <Button
              onClick={() => navigate('/enquiry/new')}
              className="bg-[#1e3a5f] hover:bg-[#162c46] text-white rounded-full px-6"
              data-testid="new-enquiry-btn"
            >
              <Plus className="w-4 h-4 mr-2" />
              New Enquiry
            </Button>
          </div>
        </div>

        {/* Decision Filter Banner */}
        {decisionFilter && (
          <div className="bg-[#1e3a5f]/10 border border-[#1e3a5f]/30 rounded-xl p-4 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <FileText className="w-5 h-5 text-[#1e3a5f]" />
              <span className="text-sm font-medium text-[#1e3a5f]">
                Showing enquiries with decision: <strong>{DECISION_LABELS[decisionFilter] || decisionFilter}</strong>
                <span className="ml-2 text-slate-500">({filteredEnquiries.length} found)</span>
              </span>
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={clearDecisionFilter}
              className="text-[#1e3a5f] hover:bg-[#1e3a5f]/10 rounded-full"
            >
              <X className="w-4 h-4 mr-1" />
              Clear Filter
            </Button>
          </div>
        )}

        {/* Stats Row */}
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-2">
          {Object.entries(STATUS_CONFIG).map(([status, config]) => (
            <button
              key={status}
              onClick={() => setStatusFilter(statusFilter === status ? 'all' : status)}
              className={`p-3 rounded-xl border transition-all text-left ${
                statusFilter === status 
                  ? 'border-[#1e3a5f] bg-[#1e3a5f]/5' 
                  : 'border-slate-200 bg-white hover:border-slate-300'
              }`}
              data-testid={`filter-${status}`}
            >
              <div className="flex items-center gap-2 mb-1">
                <div className={`w-2 h-2 rounded-full ${config.color}`} />
                <span className="text-xs font-medium text-slate-500">{config.label}</span>
              </div>
              <p className="text-lg font-bold text-[#1e3a5f]">{statusCounts[status] || 0}</p>
            </button>
          ))}
        </div>

        {/* Search & Filters */}
        <div className="flex flex-col md:flex-row gap-4">
          <div className="relative flex-1">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search by project, client, or reference..."
              className="pl-10 h-11 rounded-xl"
              data-testid="search-input"
            />
          </div>
          
          <Select value={sectorFilter} onValueChange={setSectorFilter}>
            <SelectTrigger className="w-48 h-11 rounded-xl" data-testid="sector-filter">
              <SelectValue placeholder="All Sectors" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Sectors</SelectItem>
              {Object.entries(SECTOR_LABELS).map(([value, label]) => (
                <SelectItem key={value} value={value}>{label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          
          <Button
            variant="outline"
            onClick={() => setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')}
            className="h-11 rounded-xl"
          >
            <ArrowUpDown className="w-4 h-4 mr-2" />
            {sortOrder === 'desc' ? 'Newest' : 'Oldest'}
          </Button>
        </div>

        {/* Enquiries List */}
        <div className="bg-white rounded-2xl border border-slate-100 shadow-sm overflow-hidden">
          {filteredEnquiries.length > 0 ? (
            <div className="divide-y divide-slate-100">
              {filteredEnquiries.map(enquiry => {
                const status = STATUS_CONFIG[enquiry.status] || STATUS_CONFIG.new;
                return (
                  <div
                    key={enquiry.id}
                    onClick={() => navigate(`/enquiry/${enquiry.id}`)}
                    className="p-4 md:p-6 hover:bg-slate-50 cursor-pointer transition-colors"
                    data-testid={`enquiry-${enquiry.id}`}
                  >
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1 flex-wrap">
                          <span className="text-sm font-mono text-slate-500">
                            {enquiry.reference_number}
                          </span>
                          <span className={`chip ${status.color} text-white text-xs`}>
                            {status.label}
                          </span>
                          {enquiry.sector && (
                            <span className="chip chip-default text-xs">
                              {formatSectorDisplay(enquiry)}
                            </span>
                          )}
                        </div>
                        
                        <h3 className="font-heading font-semibold text-[#1e3a5f] text-lg truncate">
                          {enquiry.project_name}
                        </h3>
                        
                        <div className="flex items-center gap-4 mt-2 text-sm text-slate-500 flex-wrap">
                          <div className="flex items-center gap-1">
                            <Building2 className="w-4 h-4" />
                            <span>{enquiry.client_company || enquiry.client_name}</span>
                          </div>
                          {enquiry.estimated_value && (
                            <div className="flex items-center gap-1">
                              <TrendingUp className="w-4 h-4" />
                              <span>{formatCurrencyCompact(enquiry.estimated_value)}</span>
                            </div>
                          )}
                          <div className="flex items-center gap-1">
                            <Calendar className="w-4 h-4" />
                            <span>
                              {new Date(enquiry.created_at).toLocaleDateString('en-GB', {
                                day: 'numeric',
                                month: 'short'
                              })}
                            </span>
                          </div>
                        </div>
                      </div>
                      
                      <div className="flex items-center gap-2 flex-shrink-0">
                        {isDirector && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={(e) => {
                              e.stopPropagation();
                              setDeleteModal({ open: true, enquiry });
                            }}
                            className="text-slate-400 hover:text-red-600 hover:bg-red-50"
                            data-testid={`delete-enquiry-${enquiry.id}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        )}
                        <ChevronRight className="w-5 h-5 text-slate-400" />
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="p-12 text-center">
              <FileText className="w-12 h-12 text-slate-300 mx-auto mb-4" />
              <h3 className="font-heading font-semibold text-slate-700 mb-2">
                No enquiries found
              </h3>
              <p className="text-slate-500 mb-6">
                {searchQuery || statusFilter !== 'all' 
                  ? 'Try adjusting your filters'
                  : 'Create your first enquiry to get started'}
              </p>
              <Button
                onClick={() => navigate('/enquiry/new')}
                className="bg-[#1e3a5f] hover:bg-[#162c46] text-white rounded-full px-6"
              >
                <Plus className="w-4 h-4 mr-2" />
                New Enquiry
              </Button>
            </div>
          )}
        </div>

        {/* Delete Confirmation Modal */}
        <Dialog open={deleteModal.open} onOpenChange={(open) => !open && setDeleteModal({ open: false, enquiry: null })}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle className="text-[#1e3a5f]">Delete Enquiry</DialogTitle>
              <DialogDescription>
                Are you sure you want to delete &quot;{deleteModal.enquiry?.project_name}&quot;?
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 mt-4">
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 text-sm text-amber-800">
                <strong>Archive</strong> will hide the enquiry but keep it recoverable.
                <br />
                <strong>Permanent delete</strong> will remove it forever.
              </div>
              <div className="flex gap-3">
                <Button
                  variant="outline"
                  onClick={() => setDeleteModal({ open: false, enquiry: null })}
                  className="flex-1"
                  disabled={deleting}
                >
                  Cancel
                </Button>
                <Button
                  onClick={() => handleDeleteEnquiry(false)}
                  variant="outline"
                  className="flex-1 border-amber-300 text-amber-700 hover:bg-amber-50"
                  disabled={deleting}
                >
                  <Archive className="w-4 h-4 mr-2" />
                  Archive
                </Button>
                <Button
                  onClick={() => handleDeleteEnquiry(true)}
                  variant="destructive"
                  className="flex-1"
                  disabled={deleting}
                >
                  <Trash2 className="w-4 h-4 mr-2" />
                  Delete
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </MainLayout>
  );
};
